import { Info } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

import { shouldOfferAutoAccept } from "../helpers/auto-accept-payments";
import { useAutoAcceptPayments } from "../hooks/use-auto-accept-payments";
import { usePaymentSetupStatus } from "../hooks/use-payment-setup-status";

const AutoAcceptBannerSkeleton = () => {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-label={t("loading_text")}
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-lg border bg-background p-4"
    >
      <div className="flex flex-1 items-center gap-4">
        <Skeleton className="size-5 shrink-0 rounded-full" />
        <Skeleton className="h-4 w-full max-w-80" />
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <Skeleton className="h-9 w-full md:w-52" />
        <Skeleton className="h-9 w-full md:w-16" />
      </div>
    </div>
  );
};

export const AutoAcceptBanner = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const titleId = useId();

  const { hasPayoutAccount, isLoading: isSetupLoading } =
    usePaymentSetupStatus();
  const { status, isLoading, isSaving, pendingValue, setEnabled } =
    useAutoAcceptPayments();

  if (isSetupLoading || isLoading) {
    return <AutoAcceptBannerSkeleton />;
  }

  if (!shouldOfferAutoAccept(status, hasPayoutAccount)) {
    return null;
  }

  return (
    <section
      aria-labelledby={titleId}
      className="rounded-lg border bg-background p-4"
      data-test-id="auto-accept-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex md:flex-1 flex-row items-center gap-4">
          <Info className="size-5 shrink-0" aria-hidden="true" />
          <p id={titleId} className="flex-1 text-sm font-bold">
            {t("auto_accept_payments_title")}
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="normal-case"
            disabled={isSaving}
            loading={isSaving && pendingValue === false}
            aria-label={t("auto_accept_payments_dismiss")}
            onClick={() => setEnabled(false)}
          >
            {t("auto_accept_payments_dismiss")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="normal-case"
            disabled={isSaving}
            loading={isSaving && pendingValue === true}
            aria-label={t("auto_accept_payments_accept")}
            onClick={() =>
              setEnabled(true, {
                onSuccess: () =>
                  toast({
                    description: t("auto_accept_payments_enabled_toast"),
                  }),
              })
            }
          >
            {t("auto_accept_payments_accept")}
          </Button>
        </div>
      </div>
    </section>
  );
};
